import { XirrData } from "@/utils/types/UserXirr";
import StatCard from "../StatCard";
import {
  Calendar,
  RefreshCw,
  Wallet,
  IndianRupee,
  TrendingUp,
  ArrowUpRight,
  TrendingDown,
} from "lucide-react";

const OverviewSection: React.FC<{
  data: XirrData | null;
  financialYear: string;
  setFinancialYear: (financialYear: string) => void;
  quarter: string;
  setQuarter: (quarter: string) => void;
}> = ({ data, financialYear, setFinancialYear, quarter, setQuarter }) => {
  const QUARTERS = [
    { label: "Q1 (Apr-Jun)", value: "Q1" },
    { label: "Q2 (Jul-Sep)", value: "Q2" },
    { label: "Q3 (Oct-Dec)", value: "Q3" },
    { label: "Q4 (Jan-Mar)", value: "Q4" },
  ];

  const FINANCIAL_YEARS = [
    { label: "FY 2025-2026", value: "2025-2026" },
    { label: "FY 2024-2025", value: "2024-2025" },
    { label: "FY 2023-2024", value: "2023-2024" },
    { label: "FY 2022-2023", value: "2022-2023" },
  ];
  return (
    <>
      <div className="mb-6 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5 p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* LEFT SIDE */}
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Calendar className="h-5 w-5 text-primary" />
            </div>

            <div>
              <p className="font-medium text-secondary">Select Period</p>
              <p className="text-xs text-muted-foreground">
                Choose financial year and quarter to view data
              </p>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="flex flex-wrap items-center gap-3">
            {/* FINANCIAL YEAR */}
            <div className="flex items-center gap-2">
              <label className="whitespace-nowrap text-sm text-muted-foreground">
                Financial Year:
              </label>
              <select
                value={financialYear}
                onChange={e => setFinancialYear(e.target.value)}
                className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {FINANCIAL_YEARS.map(year => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </select>
            </div>

            {/* QUARTER */}
            <div className="flex items-center gap-2">
              <label className="whitespace-nowrap text-sm text-muted-foreground">
                Quarter:
              </label>
              <select
                value={quarter}
                onChange={e => setQuarter(e.target.value)}
                className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {QUARTERS.map(quarter => (
                  <option key={quarter.value} value={quarter.value}>
                    {quarter.label}
                  </option>
                ))}
              </select>
            </div>

            {/* REFRESH BUTTON */}
            <button className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm text-secondary transition hover:bg-muted">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Total Corpus */}
        {/* Total Corpus */}
        <StatCard
          className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 shadow-sm"
          bodyStyle={{ padding: 20 }}
          body={
            <div className="flex items-start justify-between">
              <div>
                <p className="mb-1 text-xs text-muted-foreground">
                  Total Corpus
                </p>

                <p className="text-2xl font-bold text-secondary">
                  ₹
                  {Number(data?.currentHoldingValuation ?? 0).toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </p>

                <div className="mt-1 flex items-center gap-1 text-xs text-green-600">
                  <ArrowUpRight className="h-3 w-3" />
                  <span>Net Value</span>
                </div>
              </div>

              <div className="self-center rounded-xl bg-primary/10 p-3">
                <Wallet className="h-6 w-6 text-primary" />
              </div>
            </div>
          }
        />

        {/* Total Contributed */}
        <StatCard
          className="relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm"
          bodyStyle={{ padding: 20 }}
          body={
            <div className="mt-3 flex items-start justify-between">
              <div>
                <p className="mb-1 text-xs text-muted-foreground">
                  Total Contributed
                </p>
                <p className="text-2xl font-bold text-secondary">
                  ₹
                  {Number(data?.totalContributionAmount ?? 0).toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </p>
                {/* <p className="mt-1 text-xs text-muted-foreground">
                  Since 15 Jan 2020
                </p> */}
              </div>

              <div className="self-center rounded-xl bg-blue-100 p-3">
                <IndianRupee className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          }
        />

        {/* Total Returns */}
        {/* Notional Gain/Loss */}
        <StatCard
          className={`relative overflow-hidden rounded-2xl border shadow-sm ${
            Number(data?.notionalGainLoss ?? 0) < 0
              ? "border-red-200 bg-red-50"
              : "border-green-200 bg-gradient-to-br from-green-50 to-green-100/50"
          }`}
          bodyStyle={{ padding: 20 }}
          body={
            <div className="flex items-start justify-between">
              <div>
                <p className="mb-1 text-xs text-muted-foreground">
                  Notional Gain/Loss
                </p>

                <p
                  className={`text-2xl font-bold ${
                    Number(data?.notionalGainLoss ?? 0) < 0
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {Number(data?.notionalGainLoss ?? 0) < 0 ? "-" : "+"}₹
                  {Math.abs(Number(data?.notionalGainLoss ?? 0)).toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </p>

                <p
                  className={`mt-1 text-xs ${
                    Number(data?.notionalGainLoss ?? 0) < 0
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {Number(data?.notionalGainLoss ?? 0) < 0 ? "Loss" : "Gain"}
                </p>
              </div>

              <div
                className={`self-center rounded-xl p-3 ${
                  Number(data?.notionalGainLoss ?? 0) < 0
                    ? "bg-red-100"
                    : "bg-green-100"
                }`}
              >
                {Number(data?.notionalGainLoss ?? 0) < 0 ? (
                  <TrendingDown className="h-6 w-6 text-red-600" />
                ) : (
                  <TrendingUp className="h-6 w-6 text-green-600" />
                )}
              </div>
            </div>
          }
        />

        {/* Projected at Maturity */}
        {/* <StatCard
          className="relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm"
          bodyStyle={{ padding: 20 }}
          body={
            <div className="flex items-start justify-between">
              <div>
                <p className="mb-1 text-xs text-muted-foreground">
                  Projected at Maturity
                </p>
                <p className="text-2xl font-bold text-secondary">₹45.0L</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  20 years to go
                </p>
              </div>

              <div className="rounded-xl bg-purple-100 p-3">
                <Target className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          }
        /> */}
      </div>
    </>
  );
};

export default OverviewSection;
